import moment from "moment";
import { _ } from "extensions/underscore";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { getDefaultSymbol, abbreviateNumber, amount } from "common/formats";
import { pazienHistory } from "common/components/pazienHistory";
import { FETCH_SUMMARY_DATA } from "common/actions/dashboard";

export const mergeCategory = function(supersetCategory, subsetCategory, filterInfo) {
    const supersetPlus = supersetCategory.values.filter((p) => p.operator === "=");
    const supersetMinus = supersetCategory.values.filter((p) => p.operator === "!=");
    const subsetPlus = subsetCategory.values.filter((p) => p.operator === "=");
    const subsetMinus = subsetCategory.values.filter((p) => p.operator === "!=");
    
    supersetPlus.forEach((superSetpivot) => {
        // If positive pivot is not present in the subSet need to add both + and - to negate it
        // as the superset would only contain that pivot(s) data.
        // example... if the dashboard has method:MC and the widget has method:VI that should equal 0.
        filterInfo.widgetHasConflict = subsetPlus.length !== 0;
        if (!subsetPlus.some((p) => p.value === superSetpivot.value)) {
            subsetCategory.values.push({ value: superSetpivot.value, operator: "=" });
            if (subsetPlus.length && !subsetMinus.some((p) => p.value === superSetpivot.value)) {
                subsetCategory.values.push({ value: superSetpivot.value, operator: "!=" });
            }
        } else {
            filterInfo.widgetHasConflict = false;
        }
    });
    
    supersetMinus.forEach((superSetpivot) => {
        if (!subsetMinus.some((p) => p.value === superSetpivot.value)) {
            subsetCategory.values.push(superSetpivot);
        }
    });
    
    // Remove any positive filters from subset that are not in the superset.
    if (supersetPlus.length) {
        subsetPlus.forEach((subsetPivot) => {
            if (!supersetPlus.some((p) => p.value === subsetPivot.value)) {
                const index = subsetCategory.values.findIndex((p) => p.value === subsetPivot.value);
                subsetCategory.values.splice(index, 1);
            }
        });
    }
    
    // strictly a check for the indicator.  If you have page level -Visa and widget visa need to set conflict
    // therefore if every page level -pivot has a corresponding +pivot it will return 0
    if (supersetMinus.length) {
        let conflict = true;
        supersetMinus.forEach((supersetPivot) => {
            if (!subsetPlus.some((p) => p.value === supersetPivot.value)) {
                conflict = false;
            }
        });
        filterInfo.widgetHasConflict = conflict;
    }
};

export const mergeFilters = function(superSet, subSet) {
    const checkFilterPivots = (filter) => {
        filter.value.forEach((p) => {
            p.category = checkReplacePivot(p.category);
        });
    };
    checkFilterPivots(superSet);
    checkFilterPivots(subSet);
    
    const filterInfo = {
        widgetHasFilter: false,
        widgetHasConflict: false,
        mergedString: ""
    };
    
    // if there are no widgetQualifiers then just defer to the dashboard
    if (!subSet.toString()) {
        filterInfo.mergedString = superSet.toString();
        return filterInfo;
    }
    
    if (!superSet.toString()) {
        const subsetQ = subSet.toString();
        filterInfo.widgetHasFilter = subsetQ.length;
        filterInfo.mergedString = subsetQ;
        return filterInfo;
    }
    
    superSet.value.forEach((superSetQ) => {
        filterInfo.widgetHasFilter = true;
        if (subSet.has(superSetQ.category)) {
            mergeCategory(superSetQ, subSet.value.find((Q) => Q.category === superSetQ.category), filterInfo);
        } else {
            subSet.value.push(superSetQ);
        }
    });
    
    filterInfo.mergedString = subSet.toString();
    return filterInfo;
};

export const checkReplacePivot = (pivot) => {
    if (pivot === "processor") {
        return "provider";
    }
    if (pivot === "payment_method") {
        return "method";
    }
    return pivot;
};

// eslint-disable-next-line sentence-case/sentence-case
export const getDateRangeAsString = (range) => `${moment.utc(range.startDate).format("MMMM Do YYYY")} and ${moment.utc(range.endDate).format("MMMM Do YYYY")}`;

export const getDateRangesFromQueryParams = (queryParams) => {
    const { start, end } = queryParams;
    const originalStart = moment.utc(start);
    const originalEnd = moment.utc(end);
    const days = Math.round(moment.duration(originalEnd.diff(originalStart)).asDays());
    
    return [
        {
            startDate: originalStart,
            endDate: originalEnd,
            days
        },
        {
            startDate: originalStart.clone().subtract(days, "days"),
            endDate: originalEnd.clone().subtract(days, "days"),
            days
        }
    ];
};

export const basicDrillThrough = function(props) {
    const display_options = new Qualifiers({
        value: "card:all"
    });
    
    const fragment = pazienHistory.addParamsToFragment(`${props.urlRoot}/${display_options.toString(true)}/${props.widgetQualifiers.toString(true)}`);
    const compareTo = `${moment.utc(props.dateRanges[1].startDate).format("YYYY-MM-DD")}_${moment.utc(props.dateRanges[1].endDate).format("YYYY-MM-DD")}`;
    pazienHistory.navigateWithParams(fragment, {
        trigger: true,
        extraParams: {
            compare: compareTo
        }
    });
};

export const drillThrough = function(props, category) {
    const urlRoot = props.subModel.get("urlRoot");
    const pivot = checkReplacePivot(props.first_pivot_key);
    
    const tabQualifiers = new Qualifiers({
        value: category ? `${pivot}:${Qualifiers.normalizeValue(props.pivotName)} ${props.second_pivot_key}:${Qualifiers.normalizeValue(category)}` : `${pivot}:${props.pivotName}`
    });
    
    const mergedQ = new Qualifiers({
        value: mergeFilters(tabQualifiers, props.widgetQualifiers).mergedString
    });
    
    const display_options = new Qualifiers({
        value: "card:all"
    });
    
    const fragment = pazienHistory.addParamsToFragment(`${urlRoot}/${display_options.toString(true)}/${mergedQ.toString(true)}`);
    const compareTo = `${moment.utc(props.dateRanges[1].startDate).format("YYYY-MM-DD")}_${moment.utc(props.dateRanges[1].endDate).format("YYYY-MM-DD")}`;
    pazienHistory.navigateWithParams(fragment, {
        trigger: true,
        extraParams: {
            compare: compareTo
        }
    });
};

const getAPIEndpoint = (props) => {
    const endpoint = props.subModel.get("api_endpoint");
    const pivot = props.second_pivot_key;
    return _.isFunction(endpoint) ? endpoint(pivot) : endpoint;
};

export const fetchChartData = function(range, props) {
    let tabQualifiers = new Qualifiers({});
    if (!props.skipPivots) {
        tabQualifiers = new Qualifiers({
            value: Qualifiers.normalizeValue(`${props.first_pivot_key}:${props.pivotName}`)
        });
    }
    
    const mergedQ = new Qualifiers({
        value: mergeFilters(tabQualifiers, props.widgetQualifiers).mergedString
    });
    const url = getAPIEndpoint(props);
    
    return FETCH_SUMMARY_DATA({
        url,
        start: range.startDate.toISOString(),
        end: range.endDate.toISOString(),
        q: mergedQ
    });
};

export const sortAndFillGaps = (daysInRange, startDate, data) => {
    const fills = [];
    if (daysInRange !== data.length) {
        for (let i = 0; i < daysInRange; i++) {
            const nextDay = startDate.clone().add(i, "days").format("YYYY-MM-DD");
            if (!data.some((r) => r.date_time === nextDay)) {
                fills.push({ date_time: nextDay });
            }
        }
    }
    const allData = data.concat(fills);
    return allData.sort((a, b) => moment(a.date_time) - moment(b.date_time));
};

export const formatAmountWithCurrency = (val, threshold = 0) => {
    if (Math.abs(val) > threshold) {
        if (val < 0) {
            return (
                <><span className="currency">{getDefaultSymbol()}</span>({abbreviateNumber(Math.abs(val), false, 2)})</>
            );
        }
        return (
            <><span className="currency">{getDefaultSymbol()}</span>{abbreviateNumber(val, false, 2)}</>
        );
    }
    return (
        <><span className="currency">{getDefaultSymbol()}</span>{amount(val, { currency: "" })}</>
    );
};
